import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const projects = getProjects();
  const project = projects.find(p => p.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Mobile-only ordering: active project pinned to position 0 so it's
  // always the first thing the user sees in the horizontal scroller —
  // no need to scroll to find "you are here". The sm+ grid keeps the
  // canonical order for desktop browsing.
  const activeIndex = project ? projects.findIndex(p => p.slug === project.slug) : -1;
  const mobileProjects = activeIndex >= 0 ? [...projects.slice(activeIndex), ...projects.slice(0, activeIndex)] : projects;
  // Reset the mobile scroller back to the start whenever the active
  // project changes so the freshly-promoted "first" pill is actually
  // visible — otherwise the previous scrollLeft value persists and the
  // user sees the pills already partially scrolled.
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobileScrollerRef.current) {
      mobileScrollerRef.current.scrollLeft = 0;
    }
  }, [slug]);
  // SEO — set page title to the project name + intent + brand. Mirrors the
  // pattern used in pages/CityMaterial.tsx so HashRouter pages all have
  // crawlable titles. Also sets a meta description from the project tagline
  // for search-result snippets.
  useEffect(() => {
    if (!project) return;
    const originalTitle = document.title;
    document.title = `${project.name} — Materials & Pro Tips | 206Gravel`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute('content') ?? '';
    if (metaDesc) {
      metaDesc.setAttribute('content', `${project.tagline} See the recommended materials, depths, and pro tips for a ${project.name.toLowerCase()} project in the Tacoma area.`);
    }
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute('content', originalDesc);
    };
  }, [project]);
  if (!project) {
    return <Navigate to="/" replace data-id="element-3075" data-mp="3vd7orrvliz67" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#36322F] pt-8 pb-24" data-id="element-3076" data-mp="2a0wfs8ea531s">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3077" data-mp="2ezhiqfkx20tx">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3078" data-mp="27f5hab3cptnd">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3079" data-mp="22nf3tw94aprq">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-3080" data-mp="1xplwgzw2vppv" />
          <span className="text-stone-900 dark:text-white font-medium" data-id="element-3081" data-mp="1sq9a0g94cknt">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — bold card-style tab grid. Each tab shows
            the project icon prominently with the project name as the
            primary label and typical yards as a quiet subtitle. Active
            project gets a saturated sage fill so the current context is
            unmissable; inactive tabs have a clear hover state to invite
            switching. 2 columns on mobile, 3 on sm+, 4 on md+. */}
        <div className="mb-10" data-id="element-3082" data-mp="151q60cx3qu1j">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-3" data-id="element-3083" data-mp="101lm0cwzjh6k">
            Shop by Project
          </h2>
          {/* Mobile: horizontal scrollable pill row so the project content
           stays visible below the tabs (no long vertical menu to scroll past).
           sm+: switches to a multi-column grid for desktop browsing. */}
          <div ref={mobileScrollerRef} className="flex sm:hidden gap-2 overflow-x-auto pb-2 snap-x snap-mandatory" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-3084" data-mp="2evilen0yul7m">
            <style data-id="element-3085" data-mp="3cl3r71u76gpu">{`
              .project-tabs-mobile::-webkit-scrollbar { display: none; }
            `}</style>
            {mobileProjects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all snap-start ${isActive ? 'bg-[#04A05C] border-[#04A05C] shadow-md shadow-[#04A05C]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3086" data-mp="32lmn2oulgavz">
                  <div className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15'}`} data-id="element-3087" data-mp="4bkql4b3nqad">
                    <PIcon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3088" data-mp="i8ho1olq4608" />
                  </div>
                  <div className="flex flex-col" data-id="element-3089" data-mp="3mo905mixq9yb">
                    <span className={`font-bold text-xs leading-tight whitespace-nowrap ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3090" data-mp="3mmo8c4o09545">
                      {p.name}
                    </span>
                    <span className={`text-[10px] font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3091" data-mp="3qqnlcda2tlsr">
                      {p.typicalYards}
                    </span>
                  </div>
                </Link>;
          })}
          </div>

          {/* sm+ grid */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-id="element-3092" data-mp="11yts0we36w5x">
            {projects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${isActive ? 'bg-[#04A05C] dark:bg-[#04A05C] border-[#04A05C] dark:border-[#04A05C] shadow-md shadow-[#04A05C]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10 hover:border-[#04A05C] dark:hover:border-[#22c95c] hover:shadow-sm'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3093" data-mp="1bo3logokwmw5">
                  <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15 group-hover:bg-[#d4ddcf] dark:group-hover:bg-emerald-500/25'}`} data-id="element-3094" data-mp="t0i4j38ioehp">
                    <PIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3095" data-mp="29blzw710eh7j" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3096" data-mp="3twirs2l4sst1">
                    <div className={`font-bold text-sm leading-tight ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3097" data-mp="3ov34s4ze8c80">
                      {p.name}
                    </div>
                    <div className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3098" data-mp="3f61tgs3smj0d">
                      {p.typicalYards}
                    </div>
                  </div>
                </Link>;
          })}
          </div>
        </div>

        {/* Header */}
        <div className="mb-12" data-id="element-3099" data-mp="314z68c12b2nv">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3100" data-mp="3160s7bjm99kd">
            <Icon className="w-8 h-8" data-id="element-3101" data-mp="zedtenthgadc" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3102" data-mp="zdvrbak531xi">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3103" data-mp="3a8gnajk8ep3">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3104" data-mp="87qh0cjihllh">
            <p data-id="element-3105" data-mp="3pp04ep5g8h20">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3106" data-mp="3uoct7ffvry78">
            <span className="mr-2" data-id="element-3107" data-mp="3lohex1r13n2z">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3108" data-mp="3llle95nsqkau">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3109" data-mp="3brutobusiu0f">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3110" data-mp="14zkb9nircg97">
            Recommended Materials
          </h2>
          <div className="space-y-6" data-id="element-3111" data-mp="19xvidqho4cnq">
            {project.materials.map((mat, idx) => {
            const product = getProductDetails(mat.slug);
            if (!product) return null;
            return <motion.div key={mat.slug} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className="bg-[#ffffff] dark:bg-[#36322F] rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-white/10 flex flex-col md:flex-row gap-6 items-start" data-id="element-3112" data-mp="2pinyg9btuhpc" data-mpi={idx}>
                  <div className="w-full md:w-48 h-32 shrink-0 rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800" data-id="element-3113" data-mp="394sp282y20rq">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3114" data-mp="3n30rgvir1uaa" />
                  </div>
                  <div className="flex-1" data-id="element-3115" data-mp="1squafg36cja2">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-2" data-id="element-3116" data-mp="1srm2wi1h60w4">
                      <div data-id="element-3117" data-mp="1ntari2vj7ibs">
                        <div className="flex items-center gap-2 mb-1" data-id="element-3118" data-mp="1nu3v4uz71rpp">
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-orange" data-id="element-3119" data-mp="2wvmwtvw7flj">
                            {mat.role}
                          </span>
                          <span className="text-stone-300 dark:text-stone-600" data-id="element-3120" data-mp="3p1qz0mvltpp7">
                            •
                          </span>
                          <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-3121" data-mp="3l308wzpk38bl">
                            Depth: {mat.depth}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 dark:text-white" data-id="element-3122" data-mp="bvqtw86rgtpw">
                          {product.name}
                        </h3>
                      </div>
                      <div className="text-right" data-id="element-3123" data-mp="14c9x21r0x97p">
                        <div className="text-lg font-bold text-stone-900 dark:text-white" data-id="element-3124" data-mp="14d35r89jm8sh">
                          ${product.pricePerYard}
                          <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3125" data-mp="2i8b7e4ic0oog">
                            /yd
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-stone-600 dark:text-stone-300 mb-4" data-id="element-3126" data-mp="2r7ydh3m0ainu">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3127" data-mp="2w8m1rhiuoled">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>

                    <Link to={`/shop/${product.slug}`} className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-orange/80 transition-colors" data-id="element-3128" data-mp="365gxjsnqo7oy">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3129" data-mp="3exah7nykdyir" />
                    </Link>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3130" data-mp="3ovymrn9d4l5k">
          {/* Pro Tips */}
          <div data-id="element-3131" data-mp="1ou8ubf861504">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3132" data-mp="1otqpvvbcnkh4">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3133" data-mp="1jwgrhq5zfyh5" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3134" data-mp="1yl3hqmx5ean5">
              {project.tips.map((tip, idx) => <li key={idx} className="flex items-start" data-id="element-3135" data-mp="1ttbp86ufluud" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3136" data-mp="1trs8yg8rzjsl" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3137" data-mp="27munfrrouhh0">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3138" data-mp="2ckclgn463ca8">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3139" data-mp="2cjkyu1sjsow3">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3140" data-mp="n67s65wyqort" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3141" data-mp="i7mry6hpo53d">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3142" data-mp="2tt5gftrrtixu" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3143" data-mp="2ttx9awtzh2kf">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3144" data-mp="2k3lweksbnj1e">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#57534e]" data-id="element-3145" data-mp="2b2o0kps1ehtw">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3146" data-mp="2g1qy7p5otlru">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3147" data-mp="216ax506owply">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3148" data-mp="3viizgylcc8rr">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3149" data-mp="1we10mdohb52s" />
          </Link>
        </div>
      </div>
    </main>;
}